import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { Form } from "react-router-dom";
import PageHeader from "../../app/layout/PageHeader";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { PagingParams } from "../../app/models/pagination";
import MyTextInput from "../../app/common/form/MyTextInput";
import WholeNumberInput from "../../app/common/form/WholeNumberInput";
import {
  SearchCoreTrackingIndex,
} from "../../app/models/coreTracking";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import CoreTrackingIndexTableForm from "./CoreTrackingIndexTableForm";

export default observer(function CoreTrackingIndex() {
  const { t } = useTranslation(["common", "inventory"]);
  const { coreTrackingIndexStore } = useStore();
  const {
    loadingInitial,
    pagination,
    loadCoreTrackingIndex,
    setCoreTrackingIndexPagingParams,
    groupedCoreTrackingIndexItems,
    resetCoreTrackingIndexRegistry,
    setCoreTrackingIndexPredicate,
    resetCoreTrackingIndexPredicate,
    hasCoreTrackingIndexPredicate
  } = coreTrackingIndexStore;

  useEffect(() => {
    loadCoreTrackingIndex();
  }, [loadCoreTrackingIndex]);

  function listOnClick(value: number) {
    if (value > 0 && pagination && value < pagination?.totalPages + 1) {
      setCoreTrackingIndexPagingParams(new PagingParams(value, 25));
      resetCoreTrackingIndexRegistry();
      loadCoreTrackingIndex();
    }
  }

  let initFilterValues: SearchCoreTrackingIndex = {
    partNumber: undefined,
    description: undefined,
    invoiceNumber: undefined,
    invoiceDate: undefined,
    returned: undefined,
    refunded: undefined,
  };
  const [isSearched, setIsSearched] = useState<boolean>(false);

  async function handleFormSubmit(values: SearchCoreTrackingIndex) {
    Object.keys(values).map((vk: string) => {
      const valuesAsAny = values as any;
      setIsSearched(true);
      setCoreTrackingIndexPagingParams(new PagingParams(1, 25))
      return setCoreTrackingIndexPredicate(vk, valuesAsAny[vk]);
    });
    await loadCoreTrackingIndex();
  }

  if (loadingInitial && !isSearched)
    return (
      <LoadingComponent
        content={t("coreTrackingIndex_loading", { ns: "inventory" })}
      />
    );

  return (
    <>
      <Grid stackable columns={2}>
        <Grid.Column>
          <PageHeader
            header={t("labels.coreTracking", { ns: "inventory" })}
            type="h1"
            divider={false}
            getAlerts={true}
            addTitle={true}
          />
        </Grid.Column>
        <Grid.Column textAlign="right"></Grid.Column>
      </Grid>
      <Divider />
      <Container className="page">
        <Formik initialValues={initFilterValues} onSubmit={handleFormSubmit}>
          {({ handleSubmit, values, setFieldValue }) => (
            <Form className="ui form" onSubmit={handleSubmit}>
              <Grid stackable columns={"equal"}>
                <Grid.Row>
                  <Grid.Column>
                    <WholeNumberInput
                      placeholder={t("invoicenumber", { ns: "common" })}
                      name="invoiceNumber"
                      label={`${t("invoicenumber", { ns: "common" })}: `}
                      maxLength={5000}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <label className="myLabel">
                      {`${t("labels.invoice_date", { ns: "inventory" })}:`}
                    </label>
                    <br />
                    <SemanticDatepicker
                      name="startDate"
                      format="MM/DD/YYYY"
                      placeholder={t("labels.invoice_date", {
                        ns: "inventory",
                      })}
                      value={values.invoiceDate}
                      onChange={(e, d) => {
                        setFieldValue("invoiceDate", d.value);
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <MyTextInput
                      placeholder={t("filters.partNumber", {
                        ns: "inventory",
                      })}
                      name="partNumber"
                      label={`${t("labels.partNumber", {
                        ns: "inventory",
                      })}: `}
                      autoCapitalizeFirstLetter={false}
                      maxLength={25}
                    />
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <MyTextInput
                      placeholder={t("filters.description", {
                        ns: "inventory",
                      })}
                      name="description"
                      label={`${t("labels.description", {
                        ns: "inventory",
                      })}: `}
                      autoCapitalizeFirstLetter={false}
                      maxLength={25}
                    />
                  </Grid.Column>

                  <Grid.Column className="d-flex align-items-end">
                    <div>
                      <Button
                        color="green"
                        className="save_button"
                        icon="search"
                        content={t("filters.search", { ns: "inventory" })}
                        type="submit"
                      />
                      {isSearched && hasCoreTrackingIndexPredicate() && (
                        <Button
                          color="grey"
                          className="save_button"
                          content={t("filters.clearSearch", {
                            ns: "inventory",
                          })}
                          type="button"
                          icon="cancel"
                          onClick={() => {
                            setCoreTrackingIndexPagingParams(new PagingParams(1, 25))
                            resetCoreTrackingIndexPredicate();
                            setIsSearched(false);
                          }}
                        />
                      )}
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        </Formik>
        <CoreTrackingIndexTableForm 
          listOnClick={listOnClick}
          coreTrackingItems={groupedCoreTrackingIndexItems}
          isSearched={isSearched}
        />
      </Container>
    </>
  );
});
